import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { IconPlus, IconTrash } from '@shared/ui/icons';
import {
  BlockNote,
  Control,
  FieldError,
  FieldLabel,
  Notice,
  chronumPositionKey,
  emptyPosition,
  fleetType,
  positionIssueKey,
  renamePosition,
  typo,
  type PositionDraft,
} from '@entities/fleet';

const Rows = styled.ol`
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  padding: 0;
  list-style: none;

  &:empty {
    display: none;
  }
`;

/** Строка должности: название, ключ, «убрать». В узкой панели — в столбик. */
const Row = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr) auto;
  align-items: start;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  /* Кнопка — вровень с полями: ниже подписи (строка подписи + её отступ). */
  & > .remove {
    margin-top: ${({ theme }) => theme.spacing(5.5)};
  }

  @container drawer (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) auto;

    & > .key {
      grid-column: 1;
      grid-row: 2;
    }

    & > .remove {
      grid-column: 2;
      grid-row: 1 / span 2;
      align-self: center;
      margin-top: 0;
    }
  }
`;

const KeyControl = styled(Control)`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${fleetType.body};
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing(10)};
  height: ${({ theme }) => theme.spacing(10)};
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const ConflictNotice = styled(Notice)`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

interface PositionsEditorProps {
  rows: readonly PositionDraft[];
  onChange: (rows: PositionDraft[]) => void;
  /** Ошибка строки, которую уже пора показать. */
  error: (key: string) => string | null;
  domId: (key: string) => string;
  /** Подсказки названий — должности механизаторов из паспортов. */
  suggestions: readonly string[];
  /** Должность механизатора в паспорте группы — её можно взять одной кнопкой. */
  passportPosition: string;
  /** Список должностей правили и вы, и коллега — сохранится ваш. */
  conflict: boolean;
}

const FromPassport = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};

  & > span {
    flex: 1 1 220px;
    min-width: 0;
    text-wrap: pretty;
  }

  & strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/** id поля строки должности — для подписи, ошибки и перехода к ошибке. */
export function positionFieldId(rowId: string, part: 'name' | 'key'): string {
  return positionIssueKey(rowId, part).replace(/:/g, '-');
}

/**
 * Должности Хронума группы: название и ключ, добавить и убрать. Ключ
 * подставляется из названия, пока его не правили руками.
 */
export function PositionsEditor({
  rows,
  onChange,
  error,
  domId,
  suggestions,
  passportPosition,
  conflict,
}: PositionsEditorProps) {
  const addRef = useRef<HTMLButtonElement>(null);
  const [focusRow, setFocusRow] = useState<string | null>(null);
  const listId = domId('positions-suggest');
  // Подбор по должностям сам заводит должность Хронума; у группы, где её нет
  // (заведена вручную или должность убрали), её можно взять из паспорта одной
  // кнопкой — в Хронуме профессии называются так же.
  const passport = passportPosition.trim();
  const canTakePassport =
    passport !== '' && rows.every((r) => r.name.trim() === '' && r.key.trim() === '');

  // Кнопка видна, только пока все строки пустые, — должность из паспорта встаёт вместо них.
  function takePassport() {
    onChange([renamePosition(emptyPosition(), passport)]);
    requestAnimationFrame(() => addRef.current?.focus());
  }

  // Новая строка — фокус в её название.
  useEffect(() => {
    if (!focusRow) return;
    document.getElementById(domId(positionFieldId(focusRow, 'name')))?.focus();
    setFocusRow(null);
  }, [focusRow, domId]);

  function update(rowId: string, fn: (row: PositionDraft) => PositionDraft) {
    onChange(rows.map((r) => (r.rowId === rowId ? fn(r) : r)));
  }

  function add() {
    const row = emptyPosition();
    onChange([...rows, row]);
    setFocusRow(row.rowId);
  }

  function remove(rowId: string) {
    onChange(rows.filter((r) => r.rowId !== rowId));
    requestAnimationFrame(() => addRef.current?.focus());
  }

  return (
    <>
      <BlockNote>
        {typo(
          'Часы этих должностей в Хронуме станут маш.-часами группы. Ключ — название без регистра и знаков препинания: по нему Хронум склеивает одинаковые должности разных юрлиц. Подставляется сам, а набранный вручную приводится к тому же виду.',
        )}
      </BlockNote>
      {canTakePassport && (
        <FromPassport>
          <span>
            {typo('В паспортах моделей группы должность механизатора —')}{' '}
            <strong>«{passport}»</strong>.
          </span>
          <Button type="button" $variant="secondary" $size="sm" onClick={takePassport}>
            Взять как должность Хронума
          </Button>
        </FromPassport>
      )}
      {conflict && (
        <ConflictNotice $tone="warning" role="note">
          <span className="text">
            {typo(
              'Пока вы правили, коллега тоже менял список должностей. Сохранится ваш список — проверьте его.',
            )}
          </span>
        </ConflictNotice>
      )}
      <Rows aria-label="Должности Хронума">
        {rows.map((r, i) => {
          const nameId = domId(positionFieldId(r.rowId, 'name'));
          const keyId = domId(positionFieldId(r.rowId, 'key'));
          const nameError = error(positionIssueKey(r.rowId, 'name'));
          const keyError = error(positionIssueKey(r.rowId, 'key'));
          const title = r.name.trim() || `№${i + 1}`;
          return (
            <Row key={r.rowId}>
              <div>
                <FieldLabel htmlFor={nameId}>Должность в Хронуме</FieldLabel>
                <Control
                  id={nameId}
                  value={r.name}
                  list={listId}
                  autoComplete="off"
                  placeholder="Машинист бульдозера"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? `${nameId}-error` : undefined}
                  onChange={(e) => update(r.rowId, (row) => renamePosition(row, e.target.value))}
                />
                {nameError && <FieldError id={`${nameId}-error`}>{nameError}</FieldError>}
              </div>
              <div className="key">
                <FieldLabel htmlFor={keyId}>Ключ</FieldLabel>
                <KeyControl
                  id={keyId}
                  value={r.key}
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={keyError ? true : undefined}
                  aria-describedby={keyError ? `${keyId}-error` : undefined}
                  onChange={(e) =>
                    update(r.rowId, (row) => ({ ...row, key: e.target.value, keyEdited: true }))
                  }
                  // Ключ — в том виде, в каком его сохранит сервер.
                  onBlur={() =>
                    update(r.rowId, (row) => {
                      const key = chronumPositionKey(row.key);
                      return key === row.key || key === '' ? row : { ...row, key };
                    })
                  }
                />
                {keyError && <FieldError id={`${keyId}-error`}>{keyError}</FieldError>}
              </div>
              <RemoveButton
                type="button"
                className="remove"
                aria-label={`Убрать должность ${title}`}
                title="Убрать должность"
                onClick={() => remove(r.rowId)}
              >
                <IconTrash size={16} />
              </RemoveButton>
            </Row>
          );
        })}
      </Rows>
      <Button ref={addRef} type="button" $variant="secondary" $size="sm" onClick={add}>
        <IconPlus size={16} />
        Добавить должность
      </Button>
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  );
}
